import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import SiteApi from '../../Api/SiteApi/SiteApi';
import { formatCurrency } from '../../utils/formatters';

import { isDefault } from '../../utils/template';
import InfoBox from '../common/InfoBox';
import TextInput from '../common/TextInput';
import Loader from '../loader/Loader';

// TODO: add possibility to use state values instead of DOM elements
function PromoCode({ useplace, setDiscountValue = null, ticketPrice = 0, onRequestComplete = null}) {
    const url = 'promo-code';
    const [rememberedCode, setRememberedCode] = useState({
        value: null,
        discount: null,
        bonus: null,
        lottery: null,
        lotterySlug: null,
    });
    const [isActive, setIsActive] = useState(false);
    const [isPromocodeFieldVisible, setIsPromocodeFieldVisible] = useState(false);
    const [inputCode, setInputCode] = useState('');
    const [errors, setErrors] = useState(null);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const [pickerLotterySlug, setPickerLotterySlug] = useState(null);

    useEffect(() => {
        setLoading(true);
        checkIsActivePromoCode();
        setLoading(false);
    }, []);

    useEffect(() => {
        if (
            !isDefault() &&
            rememberedCode.value &&
            (rememberedCode.bonus === 'percentage_discount' || rememberedCode.bonus === 'amount_discount') &&
            (!rememberedCode.lottery || rememberedCode.lotterySlug === pickerLotterySlug)
        ) {
            setDiscount();
        }
    }, [rememberedCode, ticketPrice]);

    function checkIsActivePromoCode() {
        SiteApi.get(url, { params: { useplace } }).then((response) => {
            setIsActive(response.data.isActive);
            setRememberedCode(response.data.rememberedCode);
            setPickerLotterySlug(document.querySelector('input[type="hidden"][name="lottery"]')?.value);
        });
    }

    function applyCode() {
        setErrors(null);
        setMessage(null);
        setLoading(true);

        SiteApi
            .post(`${url}/apply`, { code: inputCode, useplace })
            .then((response) => {
                setRememberedCode(response.data.code);
                setMessage(t('promoCodes.promoCodeHasBeenApplied'));
            })
            .catch((error) => {
                if (error?.response?.status === 403) {
                    setIsActive(false);
                }
                setErrors(error.response.data.errors);
            })
            .finally(() => {
                setLoading(false);
                if (onRequestComplete) {
                    onRequestComplete();
                }
            });
    }

    function dispatchDiscountChangedEvent() {
        document.getElementById('paymentSubmit').dispatchEvent(new Event('discountChanged'));
    }

    function setDiscount() {
        const purchaseData = document.getElementById('purchaseSum').dataset;
        const discountAmount = rememberedCode.discount;
        const discountType = rememberedCode.bonus;

        purchaseData.discount_amount = discountAmount;
        purchaseData.discount_type = discountType;

        let sumDiscounted = 0;
        if (purchaseData.discount_type === 'amount_discount') {
            sumDiscounted = purchaseData.sum - discountAmount;
        } else if (purchaseData.discount_type === 'percentage_discount') {
            sumDiscounted = purchaseData.sum - purchaseData.sum * (discountAmount / 100);
        }

        if (typeof setDiscountValue === 'function') {
            setDiscountValue(sumDiscounted);
        }

        dispatchDiscountChangedEvent();
    }

    function clearDiscount() {
        const purchaseData = document.getElementById('purchaseSum').dataset;
        purchaseData.discount_amount = '';
        purchaseData.discount_type = '';

        dispatchDiscountChangedEvent();

        if (typeof setDiscountValue === 'function') {
            setDiscountValue(null);
        }
    }

    function getInputGroup() {
        return [
            getHeader(),
            <div key="group" className="input-group promo-code">
                {getInput()}
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!(inputCode.length > 0)}
                    onClick={() => applyCode()}
                >
                    {t('promoCodes.apply')}
                </button>
                <span className="d-none" id="promo-code-not-applied-question">
                    {t('promoCodes.notAppliedQuestion')}
                </span>
            </div>,
            <>{isDefault() && errors?.code?.[0] && <div className="text-danger mt-2">{errors.code[0]}</div>}</>,
        ];
    }

    function clearCode() {
        SiteApi
            .post(`${url}/clear`, { useplace })
            .then((response) => {
                setRememberedCode({});
                setMessage(t('promoCodes.promoCodeHasBeenCleared'));
                useplace === 'purchase' && clearDiscount();
            })
            .catch((error) => {
                setErrors(error?.response?.data?.errors);
            })
            .finally(() => {
                onRequestComplete();
            });
    }

    function getRememberedCode() {
        const code = rememberedCode.value;

        return [
            getHeader(),
            <p key="promo">
                <i className="fa fa-info-circle" />{' '}
                <Trans i18nKey="promoCodes.youUsedCode" code={code}>
                    You used <b>{{ code }}</b> promo code.
                </Trans>{' '}
                {getPromoDetails()}{' '}
                <a onClick={() => clearCode()} className="promo-code-remove">
                    {t('Remove')}
                </a>
            </p>,
        ];
    }

    function getPromoDetails() {
        let message = '';

        if (rememberedCode.bonus === 'ticket') {
            const { lottery } = rememberedCode;
            if (lottery) {
                message = (
                    <Trans i18nKey="promoCodes.youWillReceiveTicket" lottery={lottery}>
                        You will receive free <b>{{ lottery }}</b> ticket after purchase.
                    </Trans>
                );
            }
        } else {
            const discount = formatDiscount();
            message = (
                <Trans i18nKey="promoCodes.youWillReceiveDiscount" discount={discount}>
                    You will receive <b>{{ discount }}</b> discount.
                </Trans>
            );
            const { lottery } = rememberedCode;
            if (lottery) {
                message = (
                    <>
                        {message}{' '}
                        <Trans i18nKey="promoCodes.promoCodeRestrictedToLottery" lottery={lottery}>
                            This promo code applies exclusively on the <b>{{ lottery }}</b> lottery.
                        </Trans>
                    </>
                );
            }
        }
        return message;
    }

    function formatDiscount() {
        let { discount } = rememberedCode;
        if (rememberedCode.bonus === 'amount_discount') {
            discount = formatCurrency(discount);
        } else if (rememberedCode.bonus === 'percentage_discount') {
            discount += '%';
        }

        return discount;
    }

    function getHeader() {
        return [
            <span key="header" className="title">
                {t('promoCodes.promoCode')}
            </span>,
            <InfoBox errors={errors} message={message} key="info-box" />,
        ];
    }

    function getCheckbox() {
        return (
            <div className="promo-code-checkbox px-1" key="promocode_checkbox">
                <input
                    type="checkbox"
                    id="i-have-promocode"
                    name="i_have_promocode"
                    className="mr-3"
                    onChange={() => {
                        setIsPromocodeFieldVisible(!isPromocodeFieldVisible);
                    }}
                />
                <label htmlFor="i-have-promocode">{t('registration.iHavePromocode')}</label>
                <br />
            </div>
        );
    }

    function getInput() {
        return (
            <TextInput
                key="promo_code"
                name="promo_code"
                placeholder={t('promoCodes.promoCode')}
                value={inputCode}
                onChange={(event) => setInputCode(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        applyCode();
                    }
                }}
            />
        );
    }

    function getContent() {
        if (rememberedCode.value) {
            return getRememberedCode();
        }

        return isActive && getInputGroup();
    }

    return <Loader isLoading={loading} elementToShow={getContent()} additionalStyles="text-dark" />;
}

export default PromoCode;
